/* eslint-disable */
import { useMemo } from "react";
import { getActivityMix } from "../../services/xboxDataService";
import { fmtEstimated, fmtShare } from "./xboxUi";
import XboxScripts from "./XboxScripts";
import XboxMethodologyPopover from "./XboxMethodologyPopover";

const XBOX_GREEN = "#107c10";
const XBOX_GREEN_LIGHT = "#20a520";
const MUTED_GREY = "#50565e";


function DirectTileLabel({ x, y, width, height, payload }) {
  if (width < 64 || height < 64) return null;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const labelLines = payload.shortLabelLines || [payload.shortLabel || payload.label];
  const labelY = cy - (labelLines.length > 1 ? 22 : 15);
  return (
    <g className="xbox-pie-direct-label" textAnchor="middle" pointerEvents="none">
      {labelLines.map((line, index) => (
        <text key={`${line}-${index}`} x={cx} y={labelY + index * 10} className="xbox-pie-label-name">{line}</text>
      ))}
      <text x={cx} y={cy + 1} className="xbox-pie-label-value">{fmtEstimated(payload.estimated)}</text>
      <text x={cx} y={cy + 16} className="xbox-pie-label-share">{fmtShare(payload.share)}</text>
    </g>
  );
}

function InteractiveSquare({ segments: allSegments, activeKey, onSelect, disabledKey }) {
  const segments = allSegments.filter((segment) => segment.observed > 0);
  const size = 270;
  const total = segments.reduce((sum, segment) => sum + segment.observed, 0);
  const firstWidth = total ? size * segments[0].observed / total : 0;
  const remainder = total - (segments[0]?.observed || 0);
  let nextY = 0;
  const tiles = segments.map((segment, index) => {
    if (index === 0) return { segment, x: 0, y: 0, width: firstWidth, height: size };
    const height = size * segment.observed / remainder;
    const tile = { segment, x: firstWidth, y: nextY, width: size - firstWidth, height };
    nextY += height;
    return tile;
  });
  return (
    <section className="xbox-pie-panel" aria-label="Xbox activity breakdown">
      <div className="xbox-pie-viz">
        <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" aria-label="Proportional Xbox activity tiles">
            {tiles.map(({ segment, ...rect }) => {
              const selected = activeKey === segment.key;
              const faded = (activeKey && !selected) || segment.key === disabledKey;
              const label = `${segment.label}, ${fmtShare(segment.share)}, ${fmtEstimated(segment.estimated)} estimated.`;
              return (
                <g
                  key={segment.key}
                  className="xbox-activity-tile"
                  opacity={faded ? 0.3 : 1}
                  cursor={segment.key === disabledKey ? "default" : "pointer"}
                  onClick={segment.key === disabledKey ? undefined : () => onSelect(segment.key)}
                  tabIndex={segment.key === disabledKey ? -1 : 0}
                  role={segment.key === disabledKey ? undefined : "button"}
                  aria-pressed={segment.key === disabledKey ? undefined : selected}
                  aria-label={segment.key === disabledKey ? label : `${label} Filter titles.`}
                  onKeyDown={segment.key === disabledKey ? undefined : (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(segment.key);
                    }
                  }}
                >
                  <title>{label}</title>
                  <rect {...rect} fill={segment.color} stroke="#080a0c" strokeWidth={2} />
                  <DirectTileLabel {...rect} payload={segment} />
                </g>
              );
            })}
        </svg>
      </div>
    </section>
  );
}

/** Tile area represents observed activity, with the same scope and Game Pass filters. */
export default function XboxActivityMix({ dataset, activityMix, filteredEstimate, mode, gamePass, onModeSelect, onGamePassSelect, onResetFilters }) {
  const mix = useMemo(() => (dataset ? getActivityMix(dataset) : []), [dataset]);
  const latest = mix.find((m) => m.snapshotUtc === dataset?.latest?.snapshotUtc) || mix[mix.length - 1];
  if (!latest) return <div className="xbox-overview-filters xbox-panel xbox-mix-skel" />;

  const filtered = activityMix || { totalObserved: 0, counts: { Game: 0, Application: 0, Unknown: 0 }, activitySegments: [], gamePassSegments: [] };
  const activitySegments = filtered.activitySegments.map((segment) => ({
    ...segment,
    shortLabel: segment.key === "apps" ? "Apps" : "Games",
    color: segment.key === "games" ? XBOX_GREEN_LIGHT : MUTED_GREY,
    share: filtered.totalObserved ? segment.observed / filtered.totalObserved * 100 : 0,
  }));
  const gamePassSegments = filtered.gamePassSegments.map((segment) => ({
    ...segment,
    shortLabel: segment.key === "otherGames" ? undefined : segment.key === "appsOther" ? "Apps" : "Game Pass",
    shortLabelLines: segment.key === "otherGames" ? ["Outside", "Game Pass"] : undefined,
    color: segment.key === "gamePass" ? XBOX_GREEN_LIGHT : segment.key === "otherGames" ? XBOX_GREEN : MUTED_GREY,
    share: filtered.totalObserved ? segment.observed / filtered.totalObserved * 100 : 0,
  }));

  const activitySelection = mode === "games" ? "games" : mode === "apps" ? "apps" : null;
  const passSelection = gamePass === "yes" ? "gamePass" : gamePass === "no" || gamePass === "unknown" ? "otherGames" : null;
  const selectActivity = (key) => {
    if (activitySelection === key) {
      onResetFilters();
      return;
    }
    onGamePassSelect(null);
    onModeSelect(key === "games" ? "games" : "apps");
  };

  const selectPass = (key) => {
    if (passSelection === key) {
      onResetFilters();
      return;
    }
    onModeSelect("games");
    onGamePassSelect(key === "gamePass" ? "yes" : "no");
  };

  const headlineUsers = filteredEstimate ?? latest.estimatedTotalActivePopulation;

  return (
    <aside className="xbox-overview-filters" aria-label="Xbox activity filters">
      <XboxScripts />
      <div className="xbox-activity-word" aria-label={`Estimated ${fmtEstimated(headlineUsers)} users`}>
        <span className="xbox-activity-est">EST.</span>
        <span className="xbox-activity-word-text">{fmtEstimated(headlineUsers)} USERS</span>
        <XboxMethodologyPopover />
      </div>
      <div className="xbox-pie-pair">
      <InteractiveSquare
        segments={activitySegments}
        activeKey={activitySelection}
        onSelect={selectActivity}
      />
      <InteractiveSquare
        segments={gamePassSegments}
        activeKey={passSelection}
        onSelect={selectPass}
        disabledKey="appsOther"
      />
      </div>
    </aside>
  );
}
